import { FieldWrapper, FlexBox } from '@dixa/lib-shared-habla';
import type {
  SearchFilterCondition,
  SearchFilterMeta,
  SearchFilterOperator,
  SearchFilterValue,
} from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { isNullishFilterOperator } from '../../../../utils/filters/filterTypeGuards';
import { useSearchFilters } from '../../searchFilters.context';
import {
  filterFieldAssets,
  filterValidationErrors,
} from '../../utils/definitions';

import { ConditionActions } from './components/actions/conditionActions';
import { ConditionField } from './components/field/conditionField';
import { ConditionOperator } from './components/operator/conditionOperator';
import styles from './filterCondition.module.scss';
import { useFilterConditionCustomAttribute } from './utils/useFilterConditionCustomAttribute';

type FilterConditionProps = {
  index: number;
  condition: SearchFilterCondition<'conversations'>;
};

/**
 * Represents a component that can be used to apply some conditions to a search
 * filter
 */
export const FilterCondition = memo(function FilterCondition({
  index,
  condition,
}: FilterConditionProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const {
    validation: { errors },
    updateCondition,
  } = useSearchFilters();

  const customAttribute = useFilterConditionCustomAttribute(condition);

  /** If an error is set from the context, we update the internal state. */
  useEffect(() => {
    if (errors[index]) {
      setError(filterValidationErrors[errors[index]]);
    } else {
      setError(null);
    }
  }, [errors, index]);

  /** Updates the condition in the context state with the specified operator. */
  const handleChangeOperator = useCallback(
    (operator: SearchFilterOperator<'conversations'>) => {
      const newCondition = {
        ...condition,
        operator,
        values: isNullishFilterOperator(operator) ? [] : condition.values,
      } as SearchFilterCondition<'conversations'>;

      updateCondition(index, newCondition);
      ampli.searchFilterOperatorUpdated({ type: condition.field, operator });
    },
    [condition, index, updateCondition],
  );

  /** Updates the condition in the context state with the specified values. */
  const handleChangeValues = useCallback(
    (
      values: SearchFilterValue<'conversations'>[],
      meta?: SearchFilterMeta<'conversations'>,
    ) => {
      const newCondition = {
        ...condition,
        values,
        meta,
      } as SearchFilterCondition<'conversations'>;

      updateCondition(index, newCondition);
    },
    [condition, index, updateCondition],
  );

  /**
   * The display label of the condition field.
   *
   * If the condition is a custom attribute, the label is resolved from the
   * custom attributes API data, otherwise the label for default attributes is
   * resolved from the static definitions.
   */
  const label = useMemo((): string => {
    if (customAttribute) {
      return customAttribute.label ?? `Custom attribute`;
    }
    return filterFieldAssets[condition.field].label;
  }, [condition, customAttribute]);

  return (
    <FieldWrapper
      className={styles.root}
      label={
        <FlexBox alignItems="center">
          <span>{label}</span>

          <ConditionOperator
            condition={condition}
            onChange={handleChangeOperator}
          />
        </FlexBox>
      }
      labelFor={`${condition.field}-${index}`}
      meta={{ error }}
      noAlert={!error}
    >
      <div className={styles.wrapper}>
        <ConditionField
          condition={condition}
          placeholder="Choose one or more values"
          disabled={isNullishFilterOperator(condition.operator)}
          onChange={handleChangeValues}
          onError={setError}
        />

        <ConditionActions index={index} condition={condition} />
      </div>
    </FieldWrapper>
  );
});
